import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FormController,
  MuiGrid,
  MuiModal,
  MuiTextField,
  MuiTypography,
} from "../../components";
import { useSiteCore } from "../../hooks/useSiteCore";
import { MAX_LENGTH_100, gridSize } from "../../helpers/constants";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { userPreferredLanguageSelector } from "../../redux/slices/appState/appStateSelector";
import { ManageLocaleLanguage } from "./Models";
import { ManageLocaleWrapper } from "./ManageLocale.style";
import { manageLocaleValidationSchema } from "./SchemaAndValidations";
import { LanguageDropDownData } from "../../api/services/pageContent/models";
import { KeyValue } from "../../helpers/types/common";
import { InformationBadge } from "../../common/informationBadge/informationBadge";
import { getPlaceHolderKey } from "./helper";

interface ManageLocaleProps {
  show: boolean;
  onSaveManageLocalCallback: (data: KeyValue) => void;
  handleCloseModal: () => void;
  localeLanguages: KeyValue;
  isPrimaryLanguageRequired?: boolean;
  title: string;
  bannerInfoMessage?: string;
  maxLength?: number;
  localNameTitle?: string;
}

export const ManageLocale: FC<ManageLocaleProps> = ({
  show,
  onSaveManageLocalCallback,
  handleCloseModal,
  localeLanguages,
  isPrimaryLanguageRequired = true,
  title,
  bannerInfoMessage,
  maxLength = MAX_LENGTH_100,
  localNameTitle,
}) => {
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const { commonLabels, fetchValidationMessage, commonValidationMessages } =
    useSiteCore();
  const { fetchLanguageDropdownValues, commonARIALabel } =
    useNavigationSiteCore();
  const languagesFormDetails = useMemo(() => {
    const list = [] as ManageLocaleLanguage[];
    const siteCoreLanguagesList = fetchLanguageDropdownValues();
    siteCoreLanguagesList.forEach((language: LanguageDropDownData) => {
      const placeholder = getPlaceHolderKey(commonLabels, language.value);
      if (
        language.value === userSelectedLanguage &&
        isPrimaryLanguageRequired
      ) {
        list.unshift({ ...language, placeholder, isRequired: true });
      } else {
        list.push({ ...language, placeholder });
      }
    });
    return list;
  }, []);

  const { control, getValues, handleSubmit } = useForm({
    mode: "all",
    defaultValues: localeLanguages,
    resolver: yupResolver(
      manageLocaleValidationSchema(
        languagesFormDetails,
        fetchValidationMessage,
      ),
    ),
  });

  const onConfirmClick = () => {
    const formValues = getValues();
    onSaveManageLocalCallback(formValues);
    handleCloseModal();
  };

  const primaryLanguageLocaleName = useWatch({
    name: userSelectedLanguage,
    control,
  });

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={title}
      hasLargeContent
      primaryButtonTitle={commonLabels.save}
      secondaryButtonTitle={commonLabels.back}
      onPrimaryButtonClick={() => handleSubmit(onConfirmClick)()}
      onSecondaryButtonClick={handleCloseModal}
      className="manage-local-modal"
    >
      <>
        {bannerInfoMessage && <InformationBadge message={bannerInfoMessage} />}

        <ManageLocaleWrapper>
          {
            <MuiTypography
              variant="h2SemiBold"
              component="h2"
              className="text-title"
            >
              {localNameTitle ? localNameTitle : commonLabels.locale}
              {` - ${primaryLanguageLocaleName ?? ""}`}
            </MuiTypography>
          }
          <form data-testid="manage-locale">
            <MuiGrid container>
              {languagesFormDetails.map(
                ({ value: localKey, label, isRequired, placeholder }) => (
                  <MuiGrid
                    key={localKey}
                    size={gridSize}
                    className="form-field"
                  >
                    <FormController control={control} name={localKey}>
                      <MuiTextField
                        label={label}
                        placeholder={placeholder}
                        isMandatory={isRequired}
                        maxLength={maxLength}
                        showCloseIcon
                        requiredARIALabel={commonARIALabel.required}
                        tooltipMessage={
                          commonValidationMessages.fieldSpecialCharactersTooltipMessage
                        }
                        tooltipHeaderTitle={
                          commonValidationMessages.allowedSpecialCharactersTitle
                        }
                        shouldTrimSpaces
                      />
                    </FormController>
                  </MuiGrid>
                ),
              )}
            </MuiGrid>
          </form>
        </ManageLocaleWrapper>
      </>
    </MuiModal>
  );
};
